import { z } from 'zod';

/**
 * Stock et matériel (CDC §13), par territoire.
 *  - chaque référence du catalogue est « stockée » ou « illimitée » (toujours disponible, jamais
 *    décrémentée) ;
 *  - stockée : quantités Disponible / Réservé / Sorti, seuil d'alerte, historique des mouvements ;
 *  - réservation automatique quand le dossier atteint le stade choisi (validation ou planification),
 *    sortie à l'installation, libération si le dossier est annulé ou renvoyé en correction ;
 *  - le commercial ne voit que Disponible / Stock faible / Indisponible, jamais les quantités ;
 *  - l'administrateur fait des ajustements tracés et prépare le matériel par journée.
 * Le stock n'empêche jamais une vente : une réservation au-delà du disponible est acceptée et
 * signalée (disponible négatif → « Indisponible », alerte).
 */
export const STOCK_INDICATORS = ['available', 'low', 'out'] as const;
export type StockIndicator = (typeof STOCK_INDICATORS)[number];
export const STOCK_INDICATOR_LABELS: Record<StockIndicator, string> = { available: 'Disponible', low: 'Stock faible', out: 'Indisponible' };

export interface StockLevel {
  itemId: string;
  territoryId: string;
  /** physiquement en stock (réservé compris) */
  onHand: number;
  reserved: number;
  /** onHand − reserved (peut être négatif : réservé au-delà du stock) */
  available: number;
  /** cumul des sorties (installations) */
  issued: number;
  threshold: number;
}

export function stockIndicator(level: Pick<StockLevel, 'available' | 'threshold'> | null | undefined): StockIndicator {
  if (!level || level.available <= 0) return 'out';
  return level.available <= level.threshold ? 'low' : 'available';
}

export const STOCK_RESERVE_STAGES = ['validated', 'to_schedule'] as const;
export type StockReserveStage = (typeof STOCK_RESERVE_STAGES)[number];
export const STOCK_RESERVE_STAGE_LABELS: Record<StockReserveStage, string> = { validated: 'à la validation du dossier', to_schedule: 'au passage « À planifier »' };

export const STOCK_MOVEMENT_KINDS = ['receipt', 'adjustment', 'reserve', 'release', 'issue'] as const;
export type StockMovementKind = (typeof STOCK_MOVEMENT_KINDS)[number];
export const STOCK_MOVEMENT_LABELS: Record<StockMovementKind, string> = {
  receipt: 'Entrée',
  adjustment: 'Ajustement',
  reserve: 'Réservation',
  release: 'Libération',
  issue: 'Sortie (installation)',
};

const qty = z.number().finite().refine((v) => Math.abs(v) <= 100_000, 'Quantité trop élevée');
export const stockMovementInput = z
  .object({
    itemId: z.string().uuid(),
    territoryId: z.string().uuid(),
    kind: z.enum(['receipt', 'adjustment']),
    quantity: qty,
    comment: z.string().trim().max(500).nullish().transform((v) => v || null),
  })
  .superRefine((v, c) => {
    if (v.kind === 'receipt' && v.quantity <= 0) c.addIssue({ code: 'custom', path: ['quantity'], message: 'Quantité reçue > 0' });
    if (v.kind === 'adjustment' && v.quantity === 0) c.addIssue({ code: 'custom', path: ['quantity'], message: 'Quantité non nulle' });
    if (v.kind === 'adjustment' && !v.comment) c.addIssue({ code: 'custom', path: ['comment'], message: 'Motif obligatoire pour un ajustement' });
  });
export const stockThresholdInput = z.object({ itemId: z.string().uuid(), territoryId: z.string().uuid(), threshold: z.number().min(0).max(100_000) });
export const stockSettingsInput = z.object({ reserveAt: z.enum(STOCK_RESERVE_STAGES) });

export interface PickingLine {
  itemId: string;
  label: string;
  quantity: number;
  unit: string;
  stocked: boolean;
}
export interface PickingIntervention {
  interventionId: string;
  dossierId: string;
  reference: string | null;
  city: string | null;
  slot: string | null;
  technicianName: string | null;
  lines: PickingLine[];
}
/** Matériel à préparer pour une journée (§13) : par intervention, et total par référence. */
export interface PickingList {
  date: string;
  interventions: PickingIntervention[];
  totals: PickingLine[];
}
